import {Injectable, SecurityContext} from '@angular/core';
import {HttpService} from './http.service';
import {lastValueFrom, Observable, of, Subject} from 'rxjs';
import {LoggedUser, LoginRequest, LoginResponse, VerifyOtpRequest} from "../models/auth.models";
import {AnagraficaDto} from "../models/anagrafica.models";
import {DomSanitizer, SafeResourceUrl} from "@angular/platform-browser";
import {DataResponse} from "../models/http.models";
import {Router} from "@angular/router";
import {environment} from "../../environments/environment.prod";
import {BrowserPlatformLocation, Location, PlatformLocation} from '@angular/common';


/**
 * @description Servizio di gestione dell'autenticazione
 */
@Injectable({
  providedIn: 'root'
})
export class AuthService {

  constructor(
    private httpService: HttpService,
    private router: Router,
    private location : Location,
    private platformLocation: BrowserPlatformLocation,
    private sanitizer: DomSanitizer
  ) {}

  private static _authToken: string | null = null;
  private static _loggedUser: LoggedUser | null = null;

  public changeUserPhoto = new Subject<any>();
  public changeUserPhoto$ = this.changeUserPhoto.asObservable();

  public getBlackListChar = new Subject<any>();
  public getBlackListChar$ = this.getBlackListChar.asObservable();

  public baseImagePath : string = "";

  private static readonly KEY_AUTH_TOKEN = 'AuthToken';

  public static get AuthToken(): string | null {
    if (!AuthService._authToken)
      AuthService._authToken = localStorage.getItem(AuthService.KEY_AUTH_TOKEN);

    return AuthService._authToken;
  }

  public get isUserAuthenticated(): boolean {
    return (AuthService.AuthToken?.length ?? 0) > 0;
  }
  public get isUserAuthenticatedObservable(): Observable<boolean> {
    return of(this.isUserAuthenticated);
  }

  public static get LoggedUser(): LoggedUser | null { return AuthService._loggedUser; }

  public async getUserPhoto() : Promise<SafeResourceUrl>{
    const response = await lastValueFrom(this.httpService.getFile(`PortalsCandidati/GetFotoUtente`));
    var blob = new Blob([response], {type: response.type});
    const blobUrl = URL.createObjectURL(blob);
    return this.sanitizer.bypassSecurityTrustResourceUrl(blobUrl);
  }

  public async getSrcFromBlob(blob : Blob): Promise<string>{
    const blobUrl = URL.createObjectURL(blob);
    //return this.sanitizer.bypassSecurityTrustResourceUrl(blobUrl);
    return this.sanitizer.sanitize(SecurityContext.RESOURCE_URL, this.sanitizer.bypassSecurityTrustResourceUrl(blobUrl)) ?? "";
  }

  public async signInAsync(model: LoginRequest): Promise<LoginResponse> {
    try {
      model.grant_type = "password";
      const response = await lastValueFrom(this.httpService.postForm('oauth/token',  model));
      if (response.access_token) {
        AuthService.handleAuthTokenStorage(response.access_token);
        this.getBlackListChar.next(null);
        // localStorage.setItem('AuthToken', response.access_token);
      }
      return response;
    } catch (err: any) {
      console.log(`SIGNIN ERROR : ${err}`);
      return { error: err.error.error.toString() };
    }
  }

  public async verifyOtpToken(model: VerifyOtpRequest): Promise<DataResponse<any>> {
    return await lastValueFrom(this.httpService.postOtp('PortalsAuth/VerifyOtpToken', model));
  }

  private static handleAuthTokenStorage(accessToken?: string) {
    AuthService._authToken = accessToken || null;
    localStorage.setItem(AuthService.KEY_AUTH_TOKEN, accessToken ?? '');
  }

  public setAuthData(anagraficaUtente : AnagraficaDto){
    AuthService._loggedUser = new LoggedUser();
    AuthService._loggedUser.id = anagraficaUtente.IdUser;
    AuthService._loggedUser.profileId = anagraficaUtente.IdAnagraficaUtente;
    AuthService._loggedUser.profileIdEnc = anagraficaUtente.IdAnagraficaUtenteEnc;
    AuthService._loggedUser.name = anagraficaUtente.CognomeNome;
    this.getUserPhoto();
  }

  //da provare ad inserire della logica per il clear di localStorage legata al currentUrl (if currentUrl != environment.prod.apiDomain)
  public async signOut() {
    var router = this.platformLocation.href;
    var routerWithoutExt = router.replace(this.platformLocation.pathname, '');
    if(routerWithoutExt != environment.apiDomain.toString())
      localStorage.clear();
    // const currentDomain = window.location.hostname; // Ottieni il dominio corrente
    // const cookies = document.cookie.split(";");
    //
    // for (let i = 0; i < cookies.length; i++) {
    //   const cookie = cookies[i].trim();
    //   const eqPos = cookie.indexOf("=");
    //   const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
    //
    //   // Verifica che il cookie appartenga al dominio corrente e al percorso specificato
    //   if (cookie.startsWith(name) && cookie.includes(currentDomain) && cookie.includes('/')) {
    //     // Elimina solo i cookie associati al dominio corrente
    //     document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;domain=" + currentDomain + ";path=/";
    //   }
    // }
    //
    // localStorage.removeItem('authToken');
    sessionStorage.clear();

    const response = await lastValueFrom(this.httpService.post('PortalsAuth/LogOut'));
    AuthService._loggedUser = null;
    AuthService._authToken = null;
  }

  public cacheAuthData(): void {
    localStorage.setItem('athtk', AuthService._authToken ?? '');
    localStorage.setItem('lgusr', JSON.stringify(AuthService._loggedUser ?? '') ?? '');
  }

  public retrieveAuthDataFromCache(): void {
    if (localStorage.getItem('athtk')) {
      AuthService._authToken = sessionStorage.getItem('athtk');
      localStorage.removeItem('athtk');
    }

    if (localStorage.getItem('lgusr')) {
      AuthService._loggedUser = JSON.parse(localStorage.getItem('lgusr') ?? '');
      localStorage.removeItem('lgusr');
    }
  }

  public static forceSetAuthToken(accessToken?: string){
    this.handleAuthTokenStorage(accessToken);
  }
}
